import {
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { useTemplateNameForm, type TemplateNameShellProps } from './useTemplateNameForm';

/**
 * The single-field template-name prompt, on a right-side sheet. Mount it only
 * while it should be open — the field seeds once, so unmounting is what resets it.
 *
 * Validation lives in `useTemplateNameForm`, not here.
 */
export function TemplateNameSheet({
  mode,
  initialName = '',
  existingSlugs = [],
  onCancel,
  onSubmit,
}: TemplateNameShellProps) {
  const { t } = useTranslation('admin');
  const { name, setName, slugIsEmpty, slugTaken, canSubmit, handleSubmit, handleKeyDown } =
    useTemplateNameForm({ mode, initialName, existingSlugs, onSubmit });

  return (
    <Sheet open onOpenChange={(next) => !next && onCancel()}>
      <SheetContent className="w-[480px] rounded-l-lg p-xl sm:max-w-none">
        <SheetHeader className="p-0">
          <SheetTitle>
            {mode === 'create'
              ? t('templatesConfig.nameModal.newEmailTemplate')
              : t('templatesConfig.nameModal.renameEmailTemplate')}
          </SheetTitle>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-1.5">
          <Label htmlFor="template-name">{t('templatesConfig.nameModal.templateName')}</Label>
          <Input
            id="template-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={handleKeyDown}
            aria-invalid={slugIsEmpty || slugTaken || undefined}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- sole field of a name prompt
            autoFocus
          />
          {slugIsEmpty && (
            <p className="text-xs text-destructive">
              {t('templatesConfig.nameModal.needsLatinCharacters')}
            </p>
          )}
          {slugTaken && (
            <p className="text-xs text-destructive">
              {t('templatesConfig.nameModal.nameAlreadyUsed')}
            </p>
          )}
        </div>
        <SheetFooter className="flex-row items-center justify-end gap-2 p-0">
          <Button variant="outline" size="lg" onClick={onCancel}>
            {t('templatesConfig.nameModal.cancel')}
          </Button>
          <Button size="lg" onClick={handleSubmit} disabled={!canSubmit}>
            {mode === 'create'
              ? t('templatesConfig.nameModal.add')
              : t('templatesConfig.nameModal.rename')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

TemplateNameSheet.displayName = 'TemplateNameSheet';
